import React, { useEffect, useMemo, useRef, useState } from "react";
import Lottie from "react-lottie";
import animationData from "../../Assets/lottie/new-animation.json";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import config from "../../config";
import { useRecoilValue } from "recoil";
import { courseAmount } from "../../utils/recoil";

function isRTL() {
  if (typeof document !== "undefined") {
    const dir =
      document.body?.dir ||
      document.documentElement?.dir ||
      document.documentElement?.getAttribute("dir");
    return dir === "rtl";
  }
  return false;
}

function isValidPhoneNumber(phoneNumber) {
  const normalizedPhone = phoneNumber.replace(/\s+/g, "");
  return /^\+?[1-9]\d{9,14}$/.test(normalizedPhone);
}

function buildInternationalPhoneNumber(dialCode, localPhoneNumber) {
  const normalizedLocalPhoneNumber = localPhoneNumber.replace(/\D/g, "");
  const trimmedLocalPhoneNumber = normalizedLocalPhoneNumber.replace(/^0+/, "");
  return `${dialCode}${trimmedLocalPhoneNumber}`;
}

const phoneConfigByCurrency = {
  INR: {
    country: "India",
    dialCode: "+91",
    placeholder: "9876543210",
    localLength: 10,
  },
  USD: {
    country: "United States",
    dialCode: "+1",
    placeholder: "5551234567",
    localLength: 10,
  },
  AED: {
    country: "United Arab Emirates",
    dialCode: "+971",
    placeholder: "501234567",
    localLength: 9,
  },
  SAR: {
    country: "Saudi Arabia",
    dialCode: "+966",
    placeholder: "512345678",
    localLength: 9,
  },
};

const AppointmentCard = ({
  onSchedule = () => {},
  showViewFullReportButton = false,
  onViewFullReport = () => {},
}) => {
  const toast = useRef(null);
  const [visible, setVisible] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("29-09-2024");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const { t } = useTranslation("result");
  const rtl = isRTL();
  const pricingDetails = useRecoilValue(courseAmount);
  const usernameCookie = Cookies.get("username", {
    domain: config.cookieDomain,
  });
  const emailCookie = Cookies.get("email", {
    domain: config.cookieDomain,
  });

  const userDetails = useMemo(
    () => ({
      username: usernameCookie ? usernameCookie.split("-").join(" ") : "",
      email: emailCookie ?? "",
    }),
    [emailCookie, usernameCookie]
  );
  const phoneFieldConfig =
    phoneConfigByCurrency[pricingDetails?.currency_code] ||
    phoneConfigByCurrency.USD;
  const [isSlotBooked, setIsSlotBooked] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const getExistingBookings = async () => {
      try {
        const response = await fetch(config.domain + "/slot-booking", {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        const rawText = await response.text();
        let parsedResponse;

        try {
          parsedResponse = JSON.parse(rawText);
        } catch (error) {
          console.error("Invalid slot booking response:", rawText);
          return;
        }

        if (!isMounted) return;

        const hasExistingBookings =
          parsedResponse?.statusCode === 200 &&
          Array.isArray(parsedResponse?.data) &&
          parsedResponse.data.length > 0;

        setIsSlotBooked(hasExistingBookings);
      } catch (error) {
        console.error("Failed to load slot bookings:", error);
      }
    };

    getExistingBookings();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetModalState = () => {
    setAppointmentDate("");
    setPhoneNumber("");
    setBookingError("");
    setBookingSuccess("");
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setVisible(false);
    resetModalState();
  };

  const handleOpen = () => {
    if (!phoneNumber.trim()) {
      setPhoneNumber("");
    }
    setVisible(true);
  };

  const renderViewFullReportButton = () =>
    showViewFullReportButton ? (
      <button
        type="button"
        onClick={onViewFullReport}
        className="rounded-full border border-[#1C75BC] bg-white px-6 py-3 font-semibold text-[#1C75BC] shadow-[0_12px_30px_rgba(28,117,188,0.12)] transition-colors hover:bg-[#EEF7FF] md-max:px-4 md-max:py-2 md-max:text-xs"
      >
        View full report
      </button>
    ) : null;

  const handleSchedule = async () => {
    const normalizedLocalPhoneNumber = phoneNumber.replace(/\D/g, "");
    const fullPhoneNumber = buildInternationalPhoneNumber(
      phoneFieldConfig.dialCode,
      phoneNumber
    );

    if (!phoneNumber.trim()) {
      setBookingError(t("appointmentModal.phoneRequired"));
      setBookingSuccess("");
      return;
    }

    if (normalizedLocalPhoneNumber.length !== phoneFieldConfig.localLength) {
      setBookingError(
        t("appointmentModal.phoneLength", {
          count: phoneFieldConfig.localLength,
        })
      );
      setBookingSuccess("");
      return;
    }

    if (!isValidPhoneNumber(fullPhoneNumber)) {
      setBookingError(t("appointmentModal.phoneInvalid"));
      setBookingSuccess("");
      return;
    }

    setIsSubmitting(true);
    setBookingError("");
    setBookingSuccess("");

    try {
      const response = await fetch(config.domain + "/slot-booking", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: {
            phoneNumber: fullPhoneNumber,
          },
        }),
      }).then((res) => res.json());

      if (response.statusCode === 200) {
        const successMessage = response?.data?.bookingTimestamp
          ? `${t("appointmentModal.success")} ${response.data.bookingTimestamp}`
          : t("appointmentModal.success");

        setIsSlotBooked(true);
        setBookingSuccess(successMessage);
        toast.current?.show({
          severity: "success",
          summary: t("appointmentModal.success"),
          detail: successMessage,
          life: 3500,
        });
        onSchedule({
          username: userDetails.username,
          email: userDetails.email,
          phoneNumber: fullPhoneNumber,
          date: appointmentDate,
          booking: response.data,
        });
        setTimeout(() => {
          handleClose();
        }, 1200);
      } else {
        setBookingError(
          response?.error?.msg || t("appointmentModal.defaultError")
        );
      }
    } catch (error) {
      setBookingError(t("appointmentModal.defaultError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="relative min-h-[20rem] md-max:min-h-[22rem] md-max:mt-10 overflow-hidden my-5 rounded-2xl border border-[#DCEBFF] shadow-[0_18px_50px_rgba(28,117,188,0.12)] bg-white">
        <img
          src={"/new-explore-card-bg-white.jpeg"}
          alt="appointment-card-bg"
          className="absolute inset-0 h-full w-full object-cover rounded-2xl"
        />
        <div className="relative z-10 flex min-h-[20rem] md-max:min-h-[22rem] w-full items-center px-10 py-8 md-max:px-5 md-max:py-6">
          <div
            className={`flex flex-col gap-4 max-w-[62%] md-max:max-w-full ${
              rtl ? "text-right" : "text-left"
            }`}
          >
            <p className="text-3xl md-max:text-lg font-bold text-[#163B63]">
              {t("appointmentCard.text1")} <br className="md:hidden" />{" "}
              {t("appointmentCard.text2")} <br className="md:hidden" />{" "}
              {t("appointmentCard.text3")}
            </p>
            <p className="text-lg md-max:text-sm text-[#4E647B] leading-relaxed">
              {t("appointmentCard.text4")}
            </p>
            {isSlotBooked ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-max max-w-full rounded-[22px] border border-[#CFE1F5] bg-[linear-gradient(135deg,#F8FBFF_0%,#EEF6FF_100%)] px-5 py-4 shadow-[0_14px_30px_rgba(28,117,188,0.12)]">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1C75BC] text-white shadow-[0_10px_20px_rgba(28,117,188,0.2)]">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 7L9 18L4 13"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#4C86C5]">
                        Appointment Status
                      </p>
                      <p className="mt-1 text-base font-bold text-[#163B63] md-max:text-sm">
                        {t("appointmentCard.alreadyBookedTitle")}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-[#4E647B] md-max:text-xs md-max:leading-5">
                        {t("appointmentCard.alreadyBookedText")}
                      </p>
                    </div>
                  </div>
                </div>
                {renderViewFullReportButton()}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleOpen}
                  className="w-max rounded-full border border-[#1C75BC] bg-[#1C75BC] px-6 py-3 font-semibold text-white shadow-[0_12px_30px_rgba(28,117,188,0.22)] transition-colors hover:bg-[#165f98] md-max:px-4 md-max:py-2 md-max:text-xs"
                >
                  {t("appointmentCard.button")}
                </button>
                {renderViewFullReportButton()}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        visible={visible && !isSlotBooked}
        onHide={handleClose}
        style={{ width: "32rem", maxWidth: "92vw" }}
        breakpoints={{ "960px": "75vw", "641px": "92vw" }}
        className="overflow-hidden [&>div]:rounded-2xl [&>div]:border [&>div]:border-[#DCEBFF] [&>div]:shadow-[0_20px_60px_rgba(28,117,188,0.16)]"
        header={t("appointmentModal.title")}
      >
        <div className="flex flex-col gap-5 pt-2">
          <p className="text-sm leading-6 text-[#5E7288]">
            {t("appointmentModal.description")}
          </p>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-[#163B63]">
              {t("appointmentModal.username")}
            </label>
            <input
              type="text"
              value={userDetails.username}
              readOnly
              className="h-12 rounded-xl border border-[#D6E4F5] bg-[#F7FAFF] px-4 text-[#163B63] outline-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-[#163B63]">
              {t("appointmentModal.email")}
            </label>
            <input
              type="email"
              value={userDetails.email}
              readOnly
              className="h-12 rounded-xl border border-[#D6E4F5] bg-[#F7FAFF] px-4 text-[#163B63] outline-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="appointment-phone"
              className="text-sm font-semibold text-[#163B63]"
            >
              {t("appointmentModal.phone")}
            </label>
            <div className="flex h-12 overflow-hidden rounded-xl border border-[#D6E4F5] bg-white">
              <div className="flex min-w-max items-center border-r border-[#D6E4F5] bg-[#F7FAFF] px-4 text-sm font-semibold text-[#163B63]">
                {phoneFieldConfig.country} {phoneFieldConfig.dialCode}
              </div>
              <input
                id="appointment-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (bookingError) {
                    setBookingError("");
                  }
                }}
                placeholder={phoneFieldConfig.placeholder}
                className="h-full w-full bg-white px-4 text-[#163B63] outline-none"
              />
            </div>
          </div>

          {/* <div className="flex flex-col gap-2">
            <label
              htmlFor="appointment-date"
              className="text-sm font-semibold text-[#163B63]"
            >
              {t("appointmentModal.date")}
            </label>
            <input
              id="appointment-date"
              type="date"
              min={new Date().toISOString().split("T")[0]}
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              className="h-12 rounded-xl border border-[#D6E4F5] bg-white px-4 text-[#163B63] outline-none"
            />
          </div> */}

          {bookingError && (
            <p className="text-sm font-medium text-[#D64545]">{bookingError}</p>
          )}

          {bookingSuccess && (
            <p className="text-sm font-medium text-[#1D8B4F]">
              {bookingSuccess}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full border border-[#C8D8EA] bg-white px-5 py-2.5 text-[#5E7288] font-medium"
            >
              {t("appointmentModal.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSchedule}
              disabled={
                isSubmitting ||
                !appointmentDate ||
                !phoneNumber.trim() ||
                phoneNumber.replace(/\D/g, "").length !==
                  phoneFieldConfig.localLength ||
                !isValidPhoneNumber(
                  buildInternationalPhoneNumber(
                    phoneFieldConfig.dialCode,
                    phoneNumber
                  )
                )
              }
              className="rounded-full border border-[#1C75BC] bg-[#1C75BC] px-5 py-2.5 text-white font-semibold shadow-[0_12px_30px_rgba(28,117,188,0.18)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? t("appointmentModal.submitting")
                : t("appointmentModal.button")}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AppointmentCard;
